import { useMemo, useState } from "react";
import { devLog, type DevLogCategory } from "@/data/devLog";

const CATEGORY_STYLE: Record<DevLogCategory, string> = {
  기능: "bg-[#C4956A]/10 text-[#8A6640] border-[#C4956A]/30",
  데이터연동: "bg-sky-50 text-sky-700 border-sky-200",
  인프라: "bg-violet-50 text-violet-700 border-violet-200",
  보안: "bg-rose-50 text-rose-700 border-rose-200",
  성능: "bg-emerald-50 text-emerald-700 border-emerald-200",
  운영: "bg-amber-50 text-amber-700 border-amber-200",
};

const CATEGORIES = Object.keys(CATEGORY_STYLE) as DevLogCategory[];

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${y}.${m}.${d}`;
}

export default function DevLogPanel() {
  const [filter, setFilter] = useState<DevLogCategory | "all">("all");

  // 최신순(배열 뒤가 최신)으로 뒤집고, 같은 날짜는 하나의 그룹으로 묶는다.
  const groups = useMemo(() => {
    const reversed = [...devLog].reverse();
    const filtered = filter === "all" ? reversed : reversed.filter((e) => e.category === filter);
    const byDate = new Map<string, typeof filtered>();
    for (const entry of filtered) {
      const arr = byDate.get(entry.date) ?? [];
      arr.push(entry);
      byDate.set(entry.date, arr);
    }
    return Array.from(byDate.entries());
  }, [filter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const e of devLog) c[e.category] = (c[e.category] ?? 0) + 1;
    return c;
  }, []);

  return (
    <div className="space-y-4">
      {/* 상단 바 */}
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-bold text-[#1C1917]">개발 현황</h3>
        <span className="text-xs text-[#8C8780]">총 {devLog.length}건 기록</span>
      </div>
      <p className="text-xs text-[#8C8780] -mt-2">
        완료된 개발 작업을 시간순으로 기록한다. 새 항목은{" "}
        <span className="font-mono text-[11px] bg-[#F0EDE8] px-1 py-0.5 rounded">src/data/devLog.ts</span>{" "}
        배열 끝에 추가한다.
      </p>

      {/* 카테고리 필터 */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 text-xs rounded-full border transition-colors whitespace-nowrap ${
            filter === "all"
              ? "bg-[#1C1917] text-white border-[#1C1917]"
              : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
          }`}
        >
          전체 {devLog.length}
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors whitespace-nowrap ${
              filter === c
                ? "bg-[#1C1917] text-white border-[#1C1917]"
                : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
            }`}
          >
            {c} {counts[c] ?? 0}
          </button>
        ))}
      </div>

      {/* 타임라인 */}
      <div className="space-y-6">
        {groups.map(([date, entries]) => (
          <div key={date} className="flex gap-4">
            <div className="w-20 shrink-0 pt-1.5">
              <span className="text-xs font-mono font-semibold text-[#8C8780]">{formatDate(date)}</span>
            </div>
            <div className="flex-1 min-w-0 space-y-2 border-l-2 border-[#F0EDE8] pl-4">
              {entries.map((entry, i) => (
                <div key={i} className="bg-white border border-[#E8E5E0] rounded-lg px-3.5 py-2.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded border whitespace-nowrap ${CATEGORY_STYLE[entry.category]}`}>
                      {entry.category}
                    </span>
                    <span className="text-xs font-semibold text-[#1C1917]">{entry.title}</span>
                  </div>
                  {entry.summary && (
                    <p className="text-xs text-[#8C8780] leading-relaxed">{entry.summary}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {groups.length === 0 && (
          <div className="py-16 text-center text-sm text-[#8C8780]">
            <i className="ri-history-line text-3xl text-[#D1CCC2] block mb-2"></i>
            해당 카테고리 기록이 없습니다
          </div>
        )}
      </div>
    </div>
  );
}
